package org.gymly.auth

import org.gymly.loginUnificado
import org.gymly.data.AdminRepo
import org.gymly.data.PortalRepo
import org.gymly.model.AdminUser
import org.gymly.model.LoginSuccess
import org.gymly.navigation.WebHistory

object WebSessionRestore {
    suspend fun restore(currentHash: String): LoginSuccess? {
        if (!loginUnificado()) return null

        SessionActivity.syncFromPersistence()
        if (SessionActivity.isIdleExpired()) {
            clearExpiredCredentials()
            return null
        }

        return when {
            WebHistory.isAdminHash(currentHash) -> restoreAdmin()
            WebHistory.isPortalHash(currentHash) -> restorePortal()
            else -> when (AuthPersistence.loadLastKind()) {
                StoredAuthKind.CLIENT -> restorePortal() ?: restoreAdmin()
                StoredAuthKind.ADMIN -> restoreAdmin() ?: restorePortal()
                null -> restoreAdmin() ?: restorePortal()
            }
        }?.also { restored ->
            if (AuthPersistence.loadLastActivityAt() == null) {
                SessionActivity.markSessionStarted()
            }
        }
    }

    fun clearExpiredCredentials() {
        AdminRepo.session.clearAuth()
        PortalRepo.session.clearAuth()
        AuthPersistence.clear(StoredAuthKind.ADMIN)
        AuthPersistence.clear(StoredAuthKind.CLIENT)
        SessionActivity.clear()
    }

    private suspend fun restoreAdmin(): LoginSuccess.Admin? {
        val token = AuthPersistence.load(StoredAuthKind.ADMIN)?.trim()?.takeIf { it.isNotEmpty() }
            ?: return null
        AdminRepo.session.accessToken = token
        // Renueva el token al recargar. Si falla (sesión vencida), fetchMe decide.
        runCatching { AdminRepo.api.refreshSession() }.onSuccess { fresh ->
            AdminRepo.session.accessToken = fresh
            AuthPersistence.save(StoredAuthKind.ADMIN, fresh)
        }
        return runCatching {
            val me = AdminRepo.api.fetchMe()
            LoginSuccess.Admin(
                AdminUser(
                    id = me.id,
                    displayName = me.displayName,
                    email = me.email,
                    role = me.role,
                    photoUrl = me.photoUrl,
                    preferredLocale = me.preferredLocale,
                    membershipOverride = me.membershipOverride,
                    permissions = me.permissions,
                    permissionsKnown = true,
                ),
            )
        }.getOrElse {
            AdminRepo.session.clearAuth()
            AuthPersistence.clear(StoredAuthKind.ADMIN)
            null
        }
    }

    private suspend fun restorePortal(): LoginSuccess.Portal? {
        val token = AuthPersistence.load(StoredAuthKind.CLIENT)?.trim()?.takeIf { it.isNotEmpty() }
            ?: return null
        PortalRepo.session.accessToken = token
        return runCatching {
            LoginSuccess.Portal(PortalRepo.api.fetchSessionUser())
        }.getOrElse {
            PortalRepo.session.clearAuth()
            AuthPersistence.clear(StoredAuthKind.CLIENT)
            null
        }
    }
}
